import LegalPage, { LegalSection } from "./LegalPage";
import { useContent } from "../lib/contentContext";

export default function Privacy() {
    const { content } = useContent();
    const biz = content.legal.business_name;
    const owner = content.legal.owner;
    const email = content.contact.email;
    const phone = content.contact.phone_display || content.contact.phone;
    const mail = (
        <a href={`mailto:${email}`} className="link-underline text-gold">
            {email}
        </a>
    );

    return (
        <LegalPage
            eyebrow="Privacy"
            title="Privacy Policy"
            updated={content.legal.effective_date}
        >
            <p>
                {biz} ("we," "us," or "our") is committed to protecting the
                privacy of our clients and website visitors. This Privacy Policy
                explains what information we collect, how we use and safeguard
                it, and the choices available to you. By accessing our website,
                requesting a consultation, or receiving services from us, you
                acknowledge that you have read and understood this policy.
            </p>

            <LegalSection title="Information we collect">
                <p>We may collect the following categories of information:</p>
                <ul className="list-disc space-y-2 pl-6">
                    <li>
                        <strong className="text-white/90">Contact &amp; identity information</strong>{" "}
                        — name, email address, phone number, mailing address,
                        date of birth, and details you provide when booking a
                        consultation, contacting us, or creating an account.
                    </li>
                    <li>
                        <strong className="text-white/90">Health &amp; treatment information</strong>{" "}
                        — medical history, medications, allergies, skin/health
                        concerns, treatment records, consent forms, and
                        photographs, which are necessary to provide safe,
                        personalized aesthetic and wellness care.
                    </li>
                    <li>
                        <strong className="text-white/90">Payment &amp; financing information</strong>{" "}
                        — billing details and, where you choose to apply for
                        financing, information processed by our third-party
                        financing partners. We do not store full card numbers on
                        our servers.
                    </li>
                    <li>
                        <strong className="text-white/90">Technical &amp; usage information</strong>{" "}
                        — IP address, device and browser type, pages visited, and
                        similar analytics data collected automatically through
                        cookies and comparable technologies.
                    </li>
                </ul>
            </LegalSection>

            <LegalSection title="How we use your information">
                <p>We use your information to:</p>
                <ul className="list-disc space-y-2 pl-6">
                    <li>Schedule, confirm, and manage appointments and consultations;</li>
                    <li>Provide, personalize, and follow up on treatments and care;</li>
                    <li>Respond to inquiries and communicate about your care;</li>
                    <li>
                        Process payments and, when requested, facilitate
                        financing applications;
                    </li>
                    <li>
                        Send appointment reminders, aftercare instructions, and —
                        with your consent — marketing or promotional messages;
                    </li>
                    <li>
                        Operate, secure, and improve our website and services; and
                    </li>
                    <li>
                        Comply with applicable legal, regulatory, and professional
                        obligations.
                    </li>
                </ul>
                <p>We never sell your personal or health information.</p>
            </LegalSection>

            <LegalSection title="Text messaging (SMS) consent">
                <p>
                    If you provide your mobile number and opt in, we may send you
                    appointment reminders, confirmations, and promotional
                    messages by text (SMS/MMS). Message and data rates may apply,
                    and message frequency varies. Consent to receive marketing
                    texts is not a condition of purchasing any service. You may
                    opt out at any time by replying <strong className="text-white/90">STOP</strong>,
                    or reply <strong className="text-white/90">HELP</strong> for
                    assistance. Opting out of marketing texts will not affect
                    transactional messages related to your care or appointments.
                </p>
            </LegalSection>

            <LegalSection title="Cookies &amp; analytics">
                <p>
                    Our website uses cookies and similar technologies to remember
                    preferences, keep the site secure, and understand how
                    visitors use our pages so we can improve them. You can control
                    cookies through your browser settings; disabling some cookies
                    may affect site functionality. For more detail, see our{" "}
                    <a href="/cookies" className="link-underline text-gold">
                        Cookie Policy
                    </a>
                    .
                </p>
            </LegalSection>

            <LegalSection title="Photographs &amp; before/after images">
                <p>
                    With your separate written consent, we may take clinical or
                    "before and after" photographs to document your treatment and,
                    where expressly authorized, to display on our website or social
                    media for educational or promotional purposes. You may decline
                    or withdraw this consent at any time; withdrawal will not
                    affect the care you receive.
                </p>
            </LegalSection>

            <LegalSection title="How we share information">
                <p>
                    We share information only as necessary and never sell it. We
                    may disclose information to:
                </p>
                <ul className="list-disc space-y-2 pl-6">
                    <li>
                        <strong className="text-white/90">Service providers</strong>{" "}
                        — trusted vendors that support our operations (secure
                        hosting, scheduling and electronic medical record systems,
                        email/SMS delivery, payment and financing partners),
                        each bound by confidentiality obligations;
                    </li>
                    <li>
                        <strong className="text-white/90">Professional advisors</strong>{" "}
                        — such as attorneys, accountants, and insurers, where
                        appropriate;
                    </li>
                    <li>
                        <strong className="text-white/90">Legal &amp; safety</strong>{" "}
                        — when required by law, regulation, subpoena, or to protect
                        the rights, safety, or property of our clients, staff, or
                        the practice.
                    </li>
                </ul>
            </LegalSection>

            <LegalSection title="Protected health information">
                <p>
                    Where applicable, health information you share as a patient is
                    handled in accordance with relevant federal and state health
                    privacy laws (which may include HIPAA). Your medical records
                    are maintained separately with heightened safeguards and are
                    used and disclosed only as permitted by law and your
                    authorizations.
                </p>
            </LegalSection>

            <LegalSection title="Data retention">
                <p>
                    We retain personal and treatment information for as long as
                    needed to provide services, maintain required medical and
                    business records, and comply with our legal, tax, and
                    professional obligations. When information is no longer needed,
                    we securely delete or de-identify it.
                </p>
            </LegalSection>

            <LegalSection title="Your privacy rights">
                <p>
                    Subject to applicable law, you may request to access, correct,
                    update, or delete your personal information, and you may object
                    to or restrict certain processing. Depending on your state of
                    residence (for example, under the California Consumer Privacy
                    Act), you may have additional rights, including the right to
                    know what information we collect and the right to
                    non-discrimination for exercising your rights. To make a
                    request, contact us at {mail}. You may also unsubscribe from
                    marketing emails via the link in each message; transactional
                    and appointment communications will still be sent.
                </p>
            </LegalSection>

            <LegalSection title="Security">
                <p>
                    We use industry-standard technical and organizational measures
                    to protect your information. However, no method of
                    transmission or storage is completely secure. Please contact
                    us immediately at {mail} if you believe your information has
                    been compromised.
                </p>
            </LegalSection>

            <LegalSection title="Children's privacy">
                <p>
                    Our website and services are not directed to children under 13,
                    and we do not knowingly collect personal information from
                    children. Certain treatments may be provided to minors only
                    with the consent and involvement of a parent or legal guardian.
                </p>
            </LegalSection>

            <LegalSection title="Third-party links">
                <p>
                    Our website and communications may contain links to third-party
                    sites (such as financing partners, scheduling tools, or social
                    media). We are not responsible for the privacy practices of
                    those sites; we encourage you to review their policies.
                </p>
            </LegalSection>

            <LegalSection title="Changes to this policy">
                <p>
                    We may update this Privacy Policy from time to time. Changes
                    become effective when posted on this page, and the "Last
                    updated" date above will reflect the most recent revision.
                    Your continued use of our website or services after changes
                    are posted constitutes acceptance of the updated policy.
                </p>
            </LegalSection>

            <LegalSection title="Contact us">
                <p>
                    If you have questions about this Privacy Policy or how we
                    handle your information, please contact:
                </p>
                <p>
                    {biz}
                    {owner ? ` · ${owner}` : ""}
                    <br />
                    Email: {mail}
                    {phone ? (
                        <>
                            <br />
                            Phone:{" "}
                            <a
                                href={`tel:${(phone || "").replace(/[^+0-9]/g, "")}`}
                                className="link-underline text-gold"
                            >
                                {phone}
                            </a>
                        </>
                    ) : null}
                </p>
                <p className="text-sm text-white/40">
                    This policy is provided as a general template for
                    informational purposes and does not constitute legal advice.
                    We recommend review by qualified legal counsel to ensure it
                    meets your specific obligations.
                </p>
            </LegalSection>
        </LegalPage>
    );
}
